import { Helmet } from "react-helmet";
import { DashboardLayout } from "../../../../../components/layouts/DashboardLayout";
import { Header } from "../../../../../components/Header";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    FaChevronRight,
    FaArrowUp,
    FaArrowDown,
    FaMagnifyingGlass,
    FaFilter,
    FaReceipt,
    FaCalendarDays,
    FaHashtag,
    FaCircleInfo,
    FaSort,
    FaArrowRightArrowLeft,
    FaArrowLeft,
} from "react-icons/fa6";
import { BtnDefault } from "../../../../../components/buttons/BtnDefault";
import { useHookTransaction } from "../../../../../hooks/useHookTransaction";
import { TransactionCreate } from "./TransactionCreate";
import { BtnBack } from "../../../../../components/buttons/BtnBack";

/* ============================================
   UTILITIES
   ============================================= */
const formatRupiah = (num) => {
    return new Intl.NumberFormat("id-ID").format(num);
};

const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

/* ============================================
   KOMPONEN: Skeleton
   ============================================= */
const SkeletonRow = ({ cols = 7 }) => (
    <tr className="animate-pulse">
        {Array.from({ length: cols }).map((_, i) => (
            <td key={i} className="px-4 py-3.5">
                <div
                    className="h-3.5 bg-slate-700/60 rounded-md"
                    style={{ width: `${35 + Math.random() * 55}%` }}
                />
            </td>
        ))}
    </tr>
);

const SkeletonCard = () => (
    <div
        className="rounded-2xl overflow-hidden animate-pulse"
        style={{
            background: "rgba(15, 23, 42, 0.5)",
            border: "1px solid rgba(51, 65, 85, 0.3)",
        }}
    >
        <div
            className="px-4 py-3 flex justify-between items-center"
            style={{
                background: "rgba(15, 23, 42, 0.7)",
                borderBottom: "1px solid rgba(51, 65, 85, 0.2)",
            }}
        >
            <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-slate-700/60" />
                <div className="h-3 bg-slate-700/50 rounded-md w-28" />
            </div>
            <div className="h-6 bg-slate-700/40 rounded-full w-16" />
        </div>
        <div className="p-4">
            <div className="flex justify-between items-start mb-3">
                <div className="space-y-2 flex-1">
                    <div className="h-5 bg-slate-700/60 rounded-md w-40" />
                    <div className="h-3 bg-slate-700/30 rounded-md w-24" />
                </div>
                <div className="text-right space-y-1">
                    <div className="h-2.5 bg-slate-700/30 rounded w-10 ml-auto" />
                    <div className="h-7 bg-slate-700/50 rounded-md w-28 ml-auto" />
                </div>
            </div>
            <div
                className="flex justify-between items-center pt-3"
                style={{ borderTop: "1px solid rgba(51, 65, 85, 0.15)" }}
            >
                <div className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded bg-slate-700/50" />
                    <div className="h-3 bg-slate-700/30 rounded w-10" />
                </div>
                <div className="h-3 bg-slate-700/30 rounded w-20" />
            </div>
        </div>
    </div>
);

/* ============================================
   KOMPONEN: Tipe Badge
   ============================================= */
const TipeBadge = ({ tipe }) => {
    const isMasuk = tipe === "masuk";
    return (
        <span
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold tracking-wide ${
                isMasuk
                    ? "bg-emerald-500/12 text-emerald-400 border border-emerald-500/20"
                    : "bg-red-500/12 text-red-400 border border-red-500/20"
            }`}
        >
            {isMasuk ? (
                <FaArrowDown className="text-[10px]" />
            ) : (
                <FaArrowUp className="text-[10px]" />
            )}
            {isMasuk ? "Masuk" : "Keluar"}
        </span>
    );
};

/* ============================================
   KOMPONEN: Metode Badge
   ============================================= */
const MetodeBadge = ({ metode }) => {
    const colorMap = {
        "Transfer Bank": "text-blue-400 bg-blue-500/10 border-blue-500/20",
        Cash: "text-amber-400 bg-amber-500/10 border-amber-500/20",
        QRIS: "text-purple-400 bg-purple-500/10 border-purple-500/20",
        "E-Wallet": "text-pink-400 bg-pink-500/10 border-pink-500/20",
    };
    const color = colorMap[metode] || "text-slate-400 bg-slate-500/10 border-slate-500/20";
    return (
        <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-[11px] font-medium border ${color}`}
        >
            {metode}
        </span>
    );
};

/* ============================================
   OPTIONS
   ============================================= */
const TIPE_OPTIONS = [
    { value: "", label: "Semua Tipe" },
    { value: "masuk", label: "Pemasukan" },
    { value: "keluar", label: "Pengeluaran" },
];

const METODE_OPTIONS = [
    { value: "", label: "Semua Metode" },
    { value: "Transfer Bank", label: "Transfer Bank" },
    { value: "Cash", label: "Cash" },
    { value: "QRIS", label: "QRIS" },
    { value: "E-Wallet", label: "E-Wallet" },
];

const KATEGORI_OPTIONS = [
    { value: "", label: "Semua Kategori" },
    { value: "Tagihan", label: "Tagihan" },
    { value: "Operasional", label: "Operasional" },
    { value: "Gaji", label: "Gaji" },
    { value: "Sewa", label: "Sewa" },
    { value: "Bandwidth", label: "Bandwidth" },
    { value: "Utilitas", label: "Utilitas" },
    { value: "Lisensi", label: "Lisensi" },
];

const SORT_OPTIONS = [
    { value: "", label: "Default" },
    { value: "tanggal-desc", label: "Terbaru" },
    { value: "tanggal-asc", label: "Terlama" },
    { value: "nominal-desc", label: "Nominal Terbesar" },
    { value: "nominal-asc", label: "Nominal Terkecil" },
];

const LIMIT_OPTIONS = [
    { value: "5", label: "5" },
    { value: "10", label: "10" },
    { value: "20", label: "20" },
    { value: "50", label: "50" },
];

/* ============================================
   KOMPONEN: Select Styled
   ============================================= */
const SelectStyled = ({ value, onChange, options, icon }) => (
    <div className="relative">
        {icon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs pointer-events-none">
                {icon}
            </div>
        )}
        <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={`w-full appearance-none text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all pr-8 ${
                icon ? "pl-9 py-2.5" : "pl-3 py-2.5"
            }`}
        >
            {options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                    {opt.label}
                </option>
            ))}
        </select>
        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs pointer-events-none">
            <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M3 4.5L6 7.5L9 4.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </div>
    </div>
);

/* ============================================
   KOMPONEN: Ringkasan Card
   ============================================= */
const SummaryCards = ({ data }) => {
    const totalMasuk = data
        .filter((d) => d.type === "masuk")
        .reduce((sum, d) => sum + d.nominal, 0);
    const totalKeluar = data
        .filter((d) => d.type === "keluar")
        .reduce((sum, d) => sum + d.nominal, 0);
    const saldo = totalMasuk - totalKeluar;
    const trxCount = data.length;

    const cards = [
        {
            label: "Total Pemasukan",
            value: totalMasuk,
            color: "text-emerald-400",
            bgIcon: "bg-emerald-500/10 border-emerald-500/20",
            icon: <FaArrowDown className="text-emerald-400 text-sm" />,
            prefix: "+",
            isCount: false,
        },
        {
            label: "Total Pengeluaran",
            value: totalKeluar,
            color: "text-red-400",
            bgIcon: "bg-red-500/10 border-red-500/20",
            icon: <FaArrowUp className="text-red-400 text-sm" />,
            prefix: "-",
            isCount: false,
        },
        {
            label: "Saldo Bersih",
            value: saldo,
            color: saldo >= 0 ? "text-cyan-400" : "text-red-400",
            bgIcon:
                saldo >= 0
                    ? "bg-cyan-500/10 border-cyan-500/20"
                    : "bg-red-500/10 border-red-500/20",
            icon: (
                <FaArrowRightArrowLeft
                    className={`text-sm ${saldo >= 0 ? "text-cyan-400" : "text-red-400"}`}
                />
            ),
            prefix: saldo >= 0 ? "+" : "",
            isCount: false,
        },
        {
            label: "Jumlah Transaksi",
            value: trxCount,
            color: "text-violet-400",
            bgIcon: "bg-violet-500/10 border-violet-500/20",
            icon: <FaReceipt className="text-violet-400 text-sm" />,
            prefix: "",
            isCount: true,
        },
    ];

    return (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {cards.map((card) => (
                <div
                    key={card.label}
                    className="rounded-2xl p-4 transition-all duration-200"
                    style={{
                        background: "rgba(15, 23, 42, 0.5)",
                        border: "1px solid rgba(51, 65, 85, 0.3)",
                    }}
                >
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                            {card.label}
                        </span>
                        <div className={`p-2 rounded-xl border ${card.bgIcon}`}>
                            {card.icon}
                        </div>
                    </div>
                    <p className={`text-xl lg:text-2xl font-extrabold ${card.color} leading-tight`}>
                        {card.prefix}
                        {card.isCount ? card.value : formatRupiah(card.value)}
                    </p>
                </div>
            ))}
        </div>
    );
};

/* ============================================
   KOMPONEN: Pagination
   ============================================= */
const Pagination = ({ meta, onPageChange }) => {
    if (!meta || meta.last_page <= 1) return null;

    const { current_page, last_page, from, to, total } = meta;

    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 5;
        let start = Math.max(1, current_page - Math.floor(maxVisible / 2));
        let end = Math.min(last_page, start + maxVisible - 1);

        if (end - start + 1 < maxVisible) {
            start = Math.max(1, end - maxVisible + 1);
        }

        if (start > 1) {
            pages.push(1);
            if (start > 2) pages.push("...");
        }

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }

        if (end < last_page) {
            if (end < last_page - 1) pages.push("...");
            pages.push(last_page);
        }

        return pages;
    };

    return (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4">
            <p className="text-xs text-slate-500">
                Menampilkan <span className="text-slate-400 font-medium">{from ?? 0}</span>{" "}
                - <span className="text-slate-400 font-medium">{to ?? 0}</span>{" "}
                dari <span className="text-slate-400 font-medium">{total ?? 0}</span> data
            </p>
            <div className="flex items-center gap-1.5">
                <button
                    onClick={() => onPageChange(current_page - 1)}
                    disabled={current_page <= 1}
                    className="p-2 rounded-lg border border-slate-600/40 text-slate-400 hover:bg-slate-700/50 hover:text-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                    <FaArrowLeft className="text-xs" />
                </button>
                {getPageNumbers().map((page, idx) =>
                    page === "..." ? (
                        <span key={`dots-${idx}`} className="px-2 text-slate-500 text-xs">
                            ...
                        </span>
                    ) : (
                        <button
                            key={page}
                            onClick={() => onPageChange(page)}
                            className={`min-w-[36px] h-9 rounded-lg text-sm font-medium transition-all ${
                                page === current_page
                                    ? "bg-cyan-500/15 text-cyan-400 border border-cyan-500/30"
                                    : "text-slate-400 hover:bg-slate-700/50 hover:text-slate-200 border border-transparent"
                            }`}
                        >
                            {page}
                        </button>
                    )
                )}
                <button
                    onClick={() => onPageChange(current_page + 1)}
                    disabled={current_page >= last_page}
                    className="p-2 rounded-lg border border-slate-600/40 text-slate-400 hover:bg-slate-700/50 hover:text-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                    <FaChevronRight className="text-xs" />
                </button>
            </div>
        </div>
    );
};

/* ============================================
   MAIN: Transaction
   ============================================= */
export const Transaction = () => {
    const [showSidebar, setShowSidebar] = useState(true);
    const navigate = useNavigate();
    const [selectedIds, setSelectedIds] = useState([]);
    const [openModalCreate, setOpenModalCreate] = useState(false);

    // ✅ AMBIL SEMUA DARI HOOK — tidak ada state duplikat
    const {
        data,
        meta,
        loading,
        error,
        params,
        updateParams,
        resetParams,
        refreshData,
    } = useHookTransaction();

    // ✅ Helper: update params dan reset ke page 1 (untuk filter/search)
    const handleFilterChange = (newParams) => {
        updateParams({ ...newParams, page: 1 });
    };

    // ✅ Helper khusus ganti halaman
    const handlePageChange = (page) => {
        updateParams({ page });
    };

    // ✅ Active filter count — langsung dari params hook
    const activeFilterCount = [params.type, params.metode, params.category_id].filter(
        Boolean
    ).length;

    // ✅ Cek apakah ada search aktif (untuk pesan empty state)
    const hasActiveSearch = Boolean(params.search);

    /* ---------- Column Config ---------- */
    const columns = [
        { key: "no", label: "No", align: "center", width: "55px" },
        { key: "tipe", label: "Tipe", align: "center", width: "110px" },
        { key: "tanggal", label: "Tanggal", align: "left" },
        { key: "category", label: "Kategori", align: "center", width: "110px" },
        { key: "code", label: "Kode", align: "left" },
        { key: "nominal", label: "Nominal", align: "right" },
        { key: "metode", label: "Metode", align: "center", width: "130px" },
        { key: "keterangan", label: "Keterangan", align: "left" },
    ];

    // ✅ Toggle select all
    const toggleSelectAll = () => {
        if (selectedIds.length === data?.length && data?.length > 0) {
            setSelectedIds([]);
        } else {
            setSelectedIds(data?.map((item) => item.id) ?? []);
        }
    };

    // ✅ Toggle select single
    const toggleSelectSingle = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        );
    };

    return (
        <>
            <Helmet>
                <title>Transaksi Keuangan - DANELA NET</title>
                <meta
                    name="description"
                    content="Kelola data transaksi keuangan, pantau pemasukan dan pengeluaran, serta filter berdasarkan tipe dan metode pembayaran."
                />
            </Helmet>

            <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
                {/* ===== Header ===== */}
                <Header toggleSidebar={() => setShowSidebar(!showSidebar)}>
                    <div className="flex items-center gap-3">
                        <BtnBack />
                        <h2 className="text-base md:text-xl font-bold text-gray-100">
                            Transaksi Keuangan
                        </h2>
                        <span className="hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                            {meta?.total ?? 0} data
                        </span>
                    </div>
                    <BtnDefault onClick={() => setOpenModalCreate(true)}>
                        <span>Tambah Data</span>
                    </BtnDefault>
                </Header>

                {/* ===== Content ===== */}
                <main className="p-4 lg:p-6 space-y-5">
                    {/* ===== Summary Cards ===== */}
                    {!loading && data?.length > 0 && <SummaryCards data={data} />}

                    {/* ===== Search & Filter ===== */}
                    <section
                        className="rounded-2xl p-4 sm:p-5"
                        style={{
                            background: "rgba(15, 23, 42, 0.5)",
                            border: "1px solid rgba(51, 65, 85, 0.3)",
                            backdropFilter: "blur(12px)",
                        }}
                        aria-label="Filter dan pencarian"
                    >
                        <div className="flex flex-col gap-4">
                            {/* Row 1: Search + Sort + Limit */}
                            <div className="flex flex-col lg:flex-row lg:items-end gap-4">
                                <div className="w-full lg:max-w-xs flex-shrink-0">
                                    <label
                                        htmlFor="search-trx"
                                        className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider"
                                    >
                                        Cari Data
                                    </label>
                                    <div className="relative">
                                        <FaMagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm" />
                                        <input
                                            id="search-trx"
                                            type="text"
                                            placeholder="Kode, keterangan, nominal..."
                                            value={params.search}
                                            onChange={(e) =>
                                                handleFilterChange({ search: e.target.value })
                                            }
                                            className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 placeholder-slate-500 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all"
                                        />
                                    </div>
                                </div>

                                <div className="w-full lg:w-44 flex-shrink-0">
                                    <label className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">
                                        Urutkan
                                    </label>
                                    <SelectStyled
                                        value={params.sort}
                                        onChange={(val) => handleFilterChange({ sort: val })}
                                        options={SORT_OPTIONS}
                                        icon={<FaSort />}
                                    />
                                </div>

                                <div className="w-full lg:w-28 flex-shrink-0">
                                    <label className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">
                                        Tampilkan
                                    </label>
                                    <SelectStyled
                                        value={String(params.per_page)}
                                        onChange={(val) =>
                                            handleFilterChange({ per_page: Number(val) })
                                        }
                                        options={LIMIT_OPTIONS}
                                    />
                                </div>
                            </div>

                            {/* Row 2: Filters */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <FaFilter className="text-xs text-slate-500" />
                                        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                                            Filter
                                        </span>
                                        {activeFilterCount > 0 && (
                                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold bg-cyan-500 text-white">
                                                {activeFilterCount}
                                            </span>
                                        )}
                                    </div>
                                    {activeFilterCount > 0 && (
                                        <button
                                            onClick={resetParams}
                                            className="text-xs text-cyan-400 hover:text-cyan-300 font-medium transition-colors"
                                        >
                                            Reset Semua
                                        </button>
                                    )}
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    <SelectStyled
                                        value={params.type}
                                        onChange={(val) => handleFilterChange({ type: val })}
                                        options={TIPE_OPTIONS}
                                    />
                                    <SelectStyled
                                        value={params.metode}
                                        onChange={(val) => handleFilterChange({ metode: val })}
                                        options={METODE_OPTIONS}
                                    />
                                    <SelectStyled
                                        value={params.category_id}
                                        onChange={(val) =>
                                            handleFilterChange({ category_id: val })
                                        }
                                        options={KATEGORI_OPTIONS}
                                    />
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* ===== Error State ===== */}
                    {error && (
                        <div
                            className="rounded-2xl p-4 flex items-center gap-3"
                            style={{
                                background: "rgba(239, 68, 68, 0.08)",
                                border: "1px solid rgba(239, 68, 68, 0.2)",
                            }}
                        >
                            <FaCircleInfo className="text-red-400 flex-shrink-0" />
                            <div>
                                <p className="text-red-400 text-sm font-medium">
                                    Gagal memuat data
                                </p>
                                <p className="text-red-400/60 text-xs mt-0.5">
                                    {error?.message || "Terjadi kesalahan pada server"}
                                </p>
                            </div>
                            <button
                                onClick={refreshData}
                                className="ml-auto text-xs px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 transition-colors"
                            >
                                Coba Lagi
                            </button>
                        </div>
                    )}

                    {/* ===== Desktop Table ===== */}
                    <section
                        className="hidden lg:block rounded-2xl overflow-hidden"
                        style={{
                            background: "rgba(15, 23, 42, 0.5)",
                            border: "1px solid rgba(51, 65, 85, 0.3)",
                            backdropFilter: "blur(12px)",
                        }}
                        aria-label="Tabel transaksi keuangan"
                    >
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                {/* Head */}
                                <thead>
                                    <tr
                                        className="border-b"
                                        style={{
                                            background: "rgba(15, 23, 42, 0.7)",
                                            borderColor: "rgba(51, 65, 85, 0.3)",
                                        }}
                                    >
                                        <th className="px-4 py-3.5 text-center w-12">
                                            <input
                                                type="checkbox"
                                                checked={
                                                    selectedIds.length === data?.length &&
                                                    data?.length > 0
                                                }
                                                onChange={toggleSelectAll}
                                                className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500/30 focus:ring-offset-0 cursor-pointer"
                                            />
                                        </th>
                                        {columns.map((col) => (
                                            <th
                                                key={col.key}
                                                className={`px-4 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-400 ${
                                                    col.align === "center"
                                                        ? "text-center"
                                                        : col.align === "right"
                                                        ? "text-right"
                                                        : "text-left"
                                                }`}
                                                style={
                                                    col.width
                                                        ? { width: col.width }
                                                        : undefined
                                                }
                                            >
                                                {col.label}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>

                                {/* Body */}
                                <tbody
                                    className="divide-y"
                                    style={{ borderColor: "rgba(51, 65, 85, 0.15)" }}
                                >
                                    {/* Loading */}
                                    {loading &&
                                        [...Array(params.per_page)].map((_, i) => (
                                            <SkeletonRow
                                                key={`skel-${i}`}
                                                cols={columns.length + 1}
                                            />
                                        ))}

                                    {/* Empty */}
                                    {!loading && data?.length === 0 && (
                                        <tr>
                                            <td
                                                colSpan={columns.length + 1}
                                                className="px-4 py-16 text-center"
                                            >
                                                <div className="flex flex-col items-center gap-3">
                                                    <div
                                                        className="w-16 h-16 rounded-2xl flex items-center justify-center"
                                                        style={{
                                                            background:
                                                                "rgba(51, 65, 85, 0.2)",
                                                            border:
                                                                "1px solid rgba(51, 65, 85, 0.2)",
                                                        }}
                                                    >
                                                        <FaReceipt className="text-2xl text-slate-600" />
                                                    </div>
                                                    <div>
                                                        <p className="text-slate-400 font-medium">
                                                            Tidak ada transaksi ditemukan
                                                        </p>
                                                        <p className="text-xs text-slate-500 mt-1">
                                                            {hasActiveSearch ||
                                                            params.type ||
                                                            params.metode ||
                                                            params.category_id
                                                                ? "Coba ubah filter atau kata kunci pencarian"
                                                                : "Belum ada data transaksi yang tersedia"}
                                                        </p>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}

                                    {/* Data Rows */}
                                    {!loading &&
                                        data?.map((item, index) => (
                                            <tr
                                                key={item.id}
                                                className="cursor-pointer transition-colors duration-150"
                                                style={{ background: "transparent" }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.background =
                                                        "rgba(0, 180, 216, 0.04)";
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.background =
                                                        "transparent";
                                                }}
                                            >
                                                {/* Checkbox */}
                                                <td className="px-4 py-3 text-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedIds.includes(item.id)}
                                                        onChange={() => toggleSelectSingle(item.id)}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500/30 focus:ring-offset-0 cursor-pointer"
                                                    />
                                                </td>

                                                {/* No — ✅ FIX: tidak mutate meta.from langsung */}
                                                <td className="px-4 py-3 text-center text-slate-500 font-mono text-xs">
                                                    {(meta?.from ?? 0) + index}
                                                </td>

                                                {/* Tipe */}
                                                <td className="px-4 py-3 text-center">
                                                    <TipeBadge tipe={item.type} />
                                                </td>

                                                {/* Tanggal */}
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-2">
                                                        <FaCalendarDays className="text-xs text-slate-500 flex-shrink-0" />
                                                        <span className="text-gray-200 text-sm">
                                                            {formatDate(item.date_time)}
                                                        </span>
                                                    </div>
                                                </td>

                                                {/* Category */}
                                                <td className="px-4 py-3 text-center min-w-32">
                                                    {item.category?.name || item.category || "-"}
                                                </td>

                                                {/* Code */}
                                                <td className="px-4 py-3">
                                                    <span className="font-mono text-xs text-cyan-400/80 bg-cyan-500/8 px-2 py-0.5 rounded">
                                                        {item.code}
                                                    </span>
                                                </td>

                                                {/* Nominal */}
                                                <td className="px-4 py-3 text-right">
                                                    <span
                                                        className={`font-bold ${
                                                            item.type === "masuk"
                                                                ? "text-emerald-400"
                                                                : "text-red-400"
                                                        }`}
                                                    >
                                                        {item.type === "masuk" ? "+" : "-"}
                                                        {formatRupiah(item.nominal)}
                                                    </span>
                                                </td>

                                                {/* Metode */}
                                                <td className="px-4 py-3 text-center">
                                                    <MetodeBadge metode={item.metode} />
                                                </td>

                                                {/* Keterangan */}
                                                <td className="px-4 py-3">
                                                    <span className="text-slate-300 text-sm line-clamp-2">
                                                        {item.description || item.keterangan || "-"}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>

                        {/* ✅ Pagination */}
                        {!loading && data?.length > 0 && (
                            <div className="px-4 pb-4">
                                <Pagination
                                    meta={meta}
                                    onPageChange={handlePageChange}
                                />
                            </div>
                        )}
                    </section>

                    {/* ===== Mobile Cards ===== */}
                    <section className="lg:hidden space-y-3" aria-label="Daftar transaksi mobile">
                        {/* Loading Cards */}
                        {loading &&
                            [...Array(params.per_page)].map((_, i) => (
                                <SkeletonCard key={`skel-card-${i}`} />
                            ))}

                        {/* Empty */}
                        {!loading && data?.length === 0 && (
                            <div
                                className="rounded-2xl p-8 text-center"
                                style={{
                                    background: "rgba(15, 23, 42, 0.5)",
                                    border: "1px solid rgba(51, 65, 85, 0.3)",
                                }}
                            >
                                <FaReceipt className="text-3xl text-slate-600 mx-auto mb-3" />
                                <p className="text-slate-400 font-medium">
                                    Tidak ada transaksi ditemukan
                                </p>
                            </div>
                        )}

                        {/* Data Cards */}
                        {!loading &&
                            data?.map((item, index) => {
                                const isMasuk = item.type === "masuk";
                                return (
                                    <div
                                        key={item.id}
                                        className="rounded-2xl overflow-hidden transition-all duration-200"
                                        style={{
                                            background: "rgba(15, 23, 42, 0.5)",
                                            border: "1px solid rgba(51, 65, 85, 0.3)",
                                        }}
                                    >
                                        {/* Card Header */}
                                        <div
                                            className="px-4 py-3 flex justify-between items-center"
                                            style={{
                                                background: "rgba(15, 23, 42, 0.7)",
                                                borderBottom:
                                                    "1px solid rgba(51, 65, 85, 0.2)",
                                            }}
                                        >
                                            <div className="flex items-center gap-2">
                                                <span className="font-mono text-xs text-slate-500">
                                                    #{(meta?.from ?? 0) + index}
                                                </span>
                                                <TipeBadge tipe={item.type} />
                                            </div>
                                            <MetodeBadge metode={item.metode} />
                                        </div>

                                        {/* Card Body */}
                                        <div className="p-4">
                                            <div className="flex justify-between items-start mb-3">
                                                <div className="space-y-2 flex-1 min-w-0">
                                                    <p className="text-gray-200 text-sm font-medium truncate">
                                                        {item.category?.name || item.category || "-"}
                                                    </p>
                                                    <div className="flex items-center gap-2 text-xs text-slate-500">
                                                        <FaCalendarDays className="flex-shrink-0" />
                                                        <span>{formatDate(item.date_time)}</span>
                                                    </div>
                                                </div>
                                                <div className="text-right ml-3 flex-shrink-0">
                                                    <span className="text-[10px] text-slate-500 uppercase tracking-wider">
                                                        Nominal
                                                    </span>
                                                    <p
                                                        className={`text-lg font-extrabold ${
                                                            isMasuk
                                                                ? "text-emerald-400"
                                                                : "text-red-400"
                                                        }`}
                                                    >
                                                        {isMasuk ? "+" : "-"}
                                                        {formatRupiah(item.nominal)}
                                                    </p>
                                                </div>
                                            </div>

                                            {item.description && (
                                                <p className="text-xs text-slate-500 mb-3 line-clamp-2">
                                                    {item.description}
                                                </p>
                                            )}

                                            <div
                                                className="flex justify-between items-center pt-3"
                                                style={{
                                                    borderTop:
                                                        "1px solid rgba(51, 65, 85, 0.15)",
                                                }}
                                            >
                                                <span className="font-mono text-xs text-cyan-400/70 bg-cyan-500/8 px-2 py-0.5 rounded">
                                                    {item.code}
                                                </span>
                                                <button
                                                    onClick={() => navigate(`/transactions/${item.id}`)}
                                                    className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-cyan-400 transition-colors"
                                                >
                                                    Detail
                                                    <FaChevronRight className="text-[10px]" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}

                        {/* Mobile Pagination */}
                        {!loading && data?.length > 0 && (
                            <Pagination meta={meta} onPageChange={handlePageChange} />
                        )}
                    </section>
                </main>
            </DashboardLayout>

            {/* ===== Modal Create ===== */}
            {openModalCreate && (
                <TransactionCreate
                    open={openModalCreate}
                    onClose={() => setOpenModalCreate(false)}
                    onSuccess={() => {
                        setOpenModalCreate(false);
                        refreshData();
                    }}
                />
            )}
        </>
    );
};